<template>
  <div id="change-password-area">
    <div class="container">
      <h1 class="registration-title">
        {{
          tokenExpired
            ? $t(`message.notifications.tokenExpired`)
            : $t(`message.newPasswordTitle`)
        }}
      </h1>
      <div class="form" v-if="!tokenExpired">
        <div class="form-row">
          <div class="password-container">
            <input
              @change="validateForm"
              :type="showPassword ? 'text' : 'password'"
              class="form-control"
              :class="{
                'is-valid': v$.form.password.$dirty && !v$.form.password.$error,
                'is-invalid':
                  v$.form.password.$dirty && v$.form.password.$error,
              }"
              :placeholder="$t(`message.password`)"
              v-model="form.password"
              aria-describedby="pswdHelp"
            />
            <div class="show-password" @click="showPassword = !showPassword">
              <i class="fas fa-eye" v-if="!showPassword"></i>
              <i class="fas fa-eye-slash" v-else></i>
            </div>
          </div>
          <div
            id="pswdHelp"
            class="form-text text-start"
            :class="{
              'text-danger': v$.form.password.$dirty && v$.form.password.$error,
            }"
          >
            {{ $t(`message.pswdHelp`) }}
          </div>
        </div>
        <div class="form-row input-group password-container">
          <input
            :type="showConfirmPassword ? 'text' : 'password'"
            class="form-control"
            :class="{
              'is-valid':
                v$.form.password.$dirty &&
                !v$.form.password.$error &&
                validConfirmPassword &&
                form.confirmPassword.length > 0,
              'is-invalid':
                (!validConfirmPassword && form.confirmPassword.length > 0) ||
                (validConfirmPassword &&
                  v$.form.password.$dirty &&
                  v$.form.password.$error),
            }"
            :placeholder="$t(`message.confirmPassword`)"
            v-model="form.confirmPassword"
          />
          <div
            class="show-password"
            @click="showConfirmPassword = !showConfirmPassword"
          >
            <div class="fas fa-eye" v-if="!showConfirmPassword"></div>
            <div class="fas fa-eye-slash" v-else></div>
          </div>
          <div
            class="invalid-feedback"
            v-if="
              form.confirmPassword.length > 0 && validConfirmPassword == false
            "
          >
            {{ $t(`message.passwordsDontMatch`) }}
          </div>
        </div>
        <div
          class="spinner-border"
          role="status"
          v-if="$store.getters['loadings/isLoading']"
        >
          <span class="visually-hidden">{{ $t(`message.loading`) }}...</span>
        </div>
        <div class="validation_area" v-else>
          <button @click="changePassword" class="pill">
            {{ $t(`message.submit`) }}
          </button>
        </div>
      </div>
      <div v-else>
        <p>
          {{ $t(`message.notifications.tokenExpiredText`) }}
        </p>
        <router-link class="d" to="/forgot-password">{{
          $t(`message.forgotPassword`)
        }}</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { required, minLength } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
export default {
  name: "ChangePassword",
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      form: {
        password: "",
        confirmPassword: "",
      },
      showPassword: false,
      showConfirmPassword: false,
      tokenExpired: false,
    };
  },
  validations() {
    return {
      form: {
        password: {
          required,
          minLength: minLength(8),
          valid: function (value) {
            const reg = /^[0-9a-zA-Z]+$/;
            return !reg.test(value);
          },
        },
      },
    };
  },
  methods: {
    validateForm: function () {
      this.v$.$touch();
    },
    changePassword: function () {
      this.v$.$touch();

      if (!this.v$.$invalid && this.validConfirmPassword) {
        // No error, send request
        this.$store
          .dispatch("users/changePassword", {
            token: this.$route.query.token,
            password: this.form.password,
          })
          .then(() => {
            this.$router.push("AppLogin");
            this.$store.dispatch(
              "notifications/addSuccess",
              { message: this.$t(`message.notifications.passwordChanged`) },
              { root: true },
            );
          })
          .catch((error) => {
            if (error.response.status == 410) {
              // Token expired
              this.tokenExpired = true;
            } else {
              this.$store.dispatch(
                "notifications/addError",
                {
                  message: this.$t(
                    `message.notifications.errorChangingPassword`,
                  ),
                },
                { root: true },
              );
            }
          })
          .finally(() => {
            this.$store.dispatch(
              "loadings/disableLoadings",
              {},
              { root: true },
            );
          });
      }
    },
  },
  computed: {
    validConfirmPassword() {
      return this.form.confirmPassword == this.form.password;
    },
  },
  mounted() {
    if (!this.$route.query.token) {
      this.$router.push({ name: "HomePage" });
    }
    this.$store
      .dispatch("users/verifyToken", {
        token: this.$route.query.token,
      })
      .then(() => {
        this.$store.dispatch("users/logout");
      })
      .catch((error) => {
        if (error.response.status == 410) {
          // Token expired
          this.tokenExpired = true;
        } else {
          // Bad Token
          this.$store.dispatch(
            "notifications/addError",
            {
              message: this.$t(`message.notifications.badLink`),
            },
            { root: true },
          );
          this.$router.push({ name: "HomePage" });
        }
      })
      .finally(() => {
        this.$store.dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
};
</script>

<style lang="scss">
.forgotten-password {
  color: $primaryColor;
  font-weight: bold;
  font-size: 0.8em;
  right: 0px;
  bottom: 0px;

  &:hover {
    cursor: pointer;
    text-decoration: underline;
    color: $primaryColorDark;
  }
}

#change-password-area {
  position: relative;
  display: flex;
  min-height: calc(100vh - 120px - 126px - 40px);
  margin-bottom: 20px;

  .container {
    margin: 0 auto;
    padding: 3em 1em;
    width: 93%;
    border-radius: 50px;
    background-color: $primaryColorLight;
    text-align: center;
    height: 100%;

    .registration-title {
      font-family: "Averia Sans Libre", sans-serif;
      color: $primaryColor;
      margin-bottom: 50px;
    }

    @media screen and (min-width: 993px) {
      .form {
        margin: 0 auto;
        width: 40%;
      }
    }

    @media (max-width: 993px) {
      .form {
        margin: 0 auto;
        width: 90%;
      }
    }

    .form-row {
      margin-bottom: 1.5rem;
    }

    .invalid-feedback {
      text-align: left;
      display: unset !important;
    }

    .password-container {
      position: relative;
      width: 100%;

      input {
        background-image: none !important;
        box-shadow: none !important;
        border-radius: 0.25rem;
      }

      .show-password {
        position: absolute;
        right: 12px;
        top: 8px;
        color: grey;
        z-index: 10;

        &:hover {
          cursor: pointer;
          color: black;
        }
      }
    }

    .validation_area {
      display: flex;
      flex-direction: column;
      align-items: center;

      .pill {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100px;
        border: 3px solid $secondaryColor;
        background-color: $secondaryColor;
        color: $whiteColor;
        height: 40px;
        border-radius: 20px;

        &:hover {
          border: 3px solid $secondaryColor;
          color: $secondaryColor;
          background-color: $backgroundColor;
        }
      }
    }
  }
}
</style>
